<template>
	<a-modal
		class="g-qrcode-nav-list-edit-modal"
		:visible="visible"
		title="链接模块编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-qrcode-nav-list-edit" @click="clearSelection">
			<div class="g-qrcode-nav-list-edit__actions" @click.stop>
				<button type="button" class="g-qrcode-nav-list-edit__batch-btn" @click="addBatch">添加一批</button>
				<button type="button" class="g-qrcode-nav-list-edit__batch-btn" @click="addOne">添加一格</button>
			</div>

			<EditListSortGroup
				ref="sortGroup"
				class="g-qrcode-nav-list-edit__list"
				@dragover="onSortDragOver"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					:data-sort-id="item.id"
					class="g-qrcode-nav-list-edit__row-wrap"
					:class="[
						{ 'is-active': selectedIndex === index },
						getEditListSortItemClass(dragId, item.id, dragOverId),
					]"
					:draggable="form.items.length > 1"
					@click.stop
					@dragstart="onItemDragStart(item.id)"
					@dragend="onItemDragEnd"
				>
					<div
						class="g-qrcode-nav-list-edit__row"
						:class="{ 'is-selected': selectedIndex === index }"
						@click="selectItem(index)"
					>
						<div class="g-qrcode-nav-list-edit__thumb">
							<UploadProgressPanel
								v-if="isUploading && uploadTargetIndex === index"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
								:status-text="''"
							/>
							<img v-else-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
							<i v-else class="mbm-iconfont mbm-tupian"></i>
						</div>
						<input
							v-model="item.text"
							type="text"
							class="g-qrcode-nav-list-edit__text-input"
							:style="{ color: item.textColor }"
							maxlength="80"
							placeholder="灯塔：那是光照进来的地方"
							@click.stop="selectItem(index)"
							@focus="selectItem(index)"
						/>
						<i class="mbm-iconfont mbm-youjiantou g-qrcode-nav-list-edit__arrow"></i>

					</div>

					<div
						v-if="selectedIndex === index"
						class="g-qrcode-nav-edit__panel g-qrcode-nav-edit__panel--arrow g-qrcode-nav-edit__panel--float"
						:class="getPanelAlignClass(index)"
						@click.stop
					>
						<div class="g-qrcode-nav-edit__link-row">
							<i class="mbm-iconfont mbm-lianjie"></i>
							<span :class="{ 'is-empty': !getJumpLinkLabel(item) }">
								{{ getJumpLinkLabel(item) || '未设置链接' }}
							</span>
						</div>
						<div class="g-qrcode-nav-edit__toolbar">
							<button type="button" class="g-qrcode-nav-edit__action" @click="triggerImageSelect">换图</button>
							<button type="button" class="g-qrcode-nav-edit__action" @click="jumpLinkVisible = true">换链接</button>
							<button
								type="button"
								class="g-qrcode-nav-edit__action g-qrcode-nav-edit__action--icon"
								title="在下方添加"
								@click="insertAfter(index)"
							>
								<i class="mbm-iconfont mbm-jia"></i>
							</button>
							<button
								type="button"
								class="g-qrcode-nav-edit__action g-qrcode-nav-edit__action--danger-text"
								:disabled="form.items.length <= 1"
								@click="deleteItem(index)"
							>删除</button>
							<div class="g-qrcode-nav-edit__colors">
								<ColorPicker
									v-model="item.textColor"
									title="文字颜色"
									@change="val => onTextColorInput(val, index)"
								/>
							</div>
						</div>
					</div>
				</div>
			</EditListSortGroup>
		</div>
		</div>

		<input
			ref="imageInput"
			type="file"
			accept="image/*"
			class="g-qrcode-nav-edit__file-input"
			@change="onImageSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-qrcode-nav-edit-modal__ok" @click="onConfirm">确定</a-button>
		</template>

		<JumpLink
			:visible="jumpLinkVisible"
			:jumpLink="selectedItem?.jumpLink || emptyJumpLink"
			:linkTypes="['qrcode']"
			@submit="onJumpLinkSubmit"
			@cancel="jumpLinkVisible = false"
		/>
	</a-modal>
</template>

<script>
	import JumpLink from '@/components/jumpLink.vue';
	import ColorPicker from './ColorPicker.vue';
	import { createEmptyJumpLink, formatJumpLinkLabel } from '../utils/jumpLink.js';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		createDefaultListItem,
		createDefaultListItems,
		getQrcodeNavImageUrl,
		normalizeQrcodeNavListData,
		replaceQrcodeNavCellImage,
	} from '../utils/qrcodeNav.js';
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import editModalMediaPlay from '../mixins/editModalMediaPlay.js';
	import { uploadHeaderMediaFiles, createUploadTasks, updateUploadTaskProgress } from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GQrcodeNavListEditModal',
		components: { EditListSortGroup, JumpLink, ColorPicker, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeQrcodeNavListData({}),
				selectedIndex: null,
				jumpLinkVisible: false,
				emptyJumpLink: createEmptyJumpLink(),
				isUploading: false,
				uploadTargetIndex: null,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			selectedItem() {
				return this.form.items[this.selectedIndex] || null;
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, 1);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					wrapSelector: '.g-qrcode-nav-list-edit__row-wrap',
					panelSelector: '.g-qrcode-nav-edit__panel--float',
				});
			},
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			getImageUrl(item) {
				return getQrcodeNavImageUrl(item?.image);
			},
			getJumpLinkLabel(item) {
				return formatJumpLinkLabel(item?.jumpLink);
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			syncForm() {
				this.form = normalizeQrcodeNavListData(this.data);
				if (
					this.selectedIndex != null
					&& this.selectedIndex >= this.form.items.length
				) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.sortGroup, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.sortGroup, this.form.items, this, result => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			addBatch() {
				this.form.items.push(...createDefaultListItems(3));
				this.selectedIndex = this.form.items.length - 3;
			},
			addOne() {
				this.form.items.push(createDefaultListItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			insertAfter(index) {
				this.form.items.splice(index + 1, 0, createDefaultListItem({}, index + 1));
				this.selectedIndex = index + 1;
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed?.image?.local) {
					revokeLocalImageUrl(removed.image.previewUrl);
				}
				this.form.items.splice(index, 1);
				if (this.selectedIndex === index) {
					this.selectedIndex = null;
				} else if (this.selectedIndex != null && this.selectedIndex > index) {
					this.selectedIndex -= 1;
				}
			},
			triggerImageSelect() {
				this.$refs.imageInput?.click();
			},
			async onImageSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || !this.selectedItem) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				const targetIndex = this.selectedIndex;
				this.uploadTargetIndex = targetIndex;
				this.uploadTasks = createUploadTasks([file]);
				this.isUploading = true;
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (targetIndex === this.selectedIndex) {
						this.form.items[targetIndex].image = replaceQrcodeNavCellImage(
							this.form.items[targetIndex].image,
							file,
							() => item,
						);
					}
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTargetIndex = null;
					this.uploadTasks = [];
				}
			},
			onTextColorInput(color, index) {
				this.form.items[index].textColor = color;
			},
			onJumpLinkSubmit(link) {
				if (this.selectedItem) {
					this.selectedItem.jumpLink = { ...link };
				}
				this.jumpLinkVisible = false;
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeQrcodeNavListData({
					items: this.form.items.map(item => ({
						...item,
						text: item.text?.trim() || '标题：描述文字',
						jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
					})),
				});
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-qrcode-nav-edit__panel);
	.edit-float-toolbar(g-qrcode-nav-edit__toolbar);
	.edit-float-action(g-qrcode-nav-edit__action);

	.g-qrcode-nav-list-edit {
		min-height: 360px;
		padding: 4px 0 12px;
	}

	.g-qrcode-nav-list-edit__actions {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
		margin-bottom: 16px;
	}

	.g-qrcode-nav-list-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;

		&:hover {
			color: #1d4ed8;
		}
	}

	.g-qrcode-nav-list-edit__list {
		max-width: 520px;
		margin: 0 auto;
		overflow: visible; 
	}

	.g-qrcode-nav-list-edit__row-wrap {
		position: relative;
		z-index: 0;
		overflow: visible;

		&.is-active {
			z-index: 2;
		}

		&.is-list-sort-dragging {
			opacity: 0.4;
		}
	}

	.g-qrcode-nav-list-edit__row {
		display: flex;
		align-items: center;
		gap: 12px;
		min-height: 72px;
		padding: 12px 4px;
		border-bottom: 1px solid #f0f0f0;
		box-sizing: border-box;
		cursor: pointer;

		&.is-selected {
			border: 1px dashed #285af9;
			border-radius: 2px;
			padding: 11px 3px;
			margin-bottom: 0;
		}
	}

	.g-qrcode-nav-list-edit__thumb {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 4px;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 20px;
			color: #bfbfbf;
		}
	}

	.g-qrcode-nav-list-edit__text-input {
		flex: 1;
		min-width: 0;
		border: none;
		outline: none;
		background: transparent;
		font-size: 14px;
		font-family: inherit;
		padding: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.g-qrcode-nav-list-edit__arrow {
		flex-shrink: 0;
		font-size: 12px;
		color: #ccc;
	}


	.g-qrcode-nav-edit__link-row {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 40px;
		padding: 0 14px;
		border-bottom: 1px solid #f0f0f0;
		font-size: 13px;
		color: #333;

		i {
			color: #999;
			font-size: 14px;
		}

		.is-empty {
			color: #999;
		}
	}

	.g-qrcode-nav-edit__action {
		&--icon i {
			font-size: 14px;
		}
	}

	.g-qrcode-nav-edit__colors {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-left: auto;
		padding-left: 8px;
	}

	.g-qrcode-nav-edit__swatch {
		position: relative;
		display: inline-block;
		width: 18px;
		height: 18px;
		border: 1px solid #b0b0b0;
		border-radius: 3px;
		cursor: pointer;
		overflow: hidden;
		flex-shrink: 0;
		box-sizing: border-box;

		&:hover {
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.25);
		}
	}

	.g-qrcode-nav-edit__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		padding: 0;
		border: none;
		opacity: 0;
		cursor: pointer;
	}

	.g-qrcode-nav-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-qrcode-nav-list-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-qrcode-nav-edit-modal__ok {

			&:hover,
			&:focus {
				background: #3868ff;
				border-color: #3868ff;
			}
		}
	}
</style>
